import { pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { useCallback, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import {
  buildRetailInvoiceLabels,
  RetailInvoicePDF,
} from 'src/components/RetailInvoice';
import { normalizeCardBrand } from 'src/components/Selectors/BuyerSelector/BuyerCard';
import { useAppContext } from 'src/contexts/AppContext';
import { useContentContext } from 'src/contexts/ContentContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { RotatingWrapper } from 'src/core/ui/AnimatingWrapper';
import { useGenerateGroupInvoicePdf } from 'src/hooks/api/useGenerateGroupInvoicePdf';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { DownloadIcon, ProcessingIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import {
  Feature,
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  PointOfSalePaymentState,
  SaleCostType,
  SaleDetails,
} from 'src/WebApiController';

import {
  useRetailSaleInvoiceData,
  useSaleInvoiceCatalogData,
} from './LaunchDownloadSaleInvoice.hooks';

export const LaunchDownloadRetailSaleInvoice = ({
  sale,
  variant,
  iconOnlyMode,
  disabled,
}: {
  sale: SaleDetails;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
}) => {
  const { loginContext } = useAppContext();
  // POS-8856: resolve invoice labels at component top (rules-of-hooks) so the async
  // download callback — which renders the PDF outside the ContentContext tree — has
  // pre-resolved localized strings to pass into <RetailInvoicePDF labels={...} />.
  const contentContext = useContentContext();
  const labels = buildRetailInvoiceLabels(contentContext);
  const [isLoading, setIsLoading] = useState(false);
  const { event, venue, buyer, eligibleToDownload } =
    useSaleInvoiceCatalogData(sale);
  const { buyerAddress, buyerPaymentMethods } = useRetailSaleInvoiceData(sale);

  // POS-8630: when this retail sale belongs to a sales group (and the viewer has the
  // SalesGroupView feature) we download the FULL multi-event GROUP invoice instead of
  // the single-sale invoice. shouldUseGroupInvoice depends on this sale's salesGroupId
  // plus the feature, so it's computed locally; the actual group-invoice generation is
  // shared via useGenerateGroupInvoicePdf (POS-8856).
  const hasCleanPaymentDisplay = useUserHasFeature(Feature.CleanPaymentDisplay);
  const hasSalesGroupViewFeature = useUserHasFeature(Feature.SalesGroupView);
  const shouldUseGroupInvoice =
    sale.salesGroupId != null && hasSalesGroupViewFeature;

  // POS-8856: shared group-invoice generation (payload + batch catalog fetch + PDF
  // build). isPreparingGroup is true while the group payload or its catalog is in flight.
  const { generateBlob: generateGroupBlob, isPreparing: isPreparingGroup } =
    useGenerateGroupInvoicePdf(sale.salesGroupId);

  const generateAndDownloadGroupPDF = useCallback(async () => {
    setIsLoading(true);
    try {
      const blob = await generateGroupBlob();
      if (!blob) {
        return;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailGroupInvoice_${sale.salesGroupId}_${format(
        new Date(),
        'yyyy-MM-dd'
      )}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setIsLoading(false);
    }
  }, [generateGroupBlob, sale.salesGroupId]);

  const generateAndDownloadRetailPDF = useCallback(async () => {
    setIsLoading(true);
    try {
      const defaultPaymentMethod =
        buyerPaymentMethods?.find((pm) => pm.isDefault) ??
        buyerPaymentMethods?.[0];
      const paymentMethodDisplay = defaultPaymentMethod
        ? hasCleanPaymentDisplay
          ? `${normalizeCardBrand(defaultPaymentMethod.cardBrand) ?? ''} ${defaultPaymentMethod.cardSummary ?? ''}`.trim()
          : `${defaultPaymentMethod.cardBrand ?? ''} ****${defaultPaymentMethod.cardSummary ?? ''}`.trim()
        : undefined;

      const totalAmount = sale.ttlNetProcs?.amt ?? 0;
      const paymentsMade = (sale.mkpLines ?? [])
        .filter(
          (line) =>
            line.type === MarketplacePaymentLineType.Proceeds &&
            line.state === MarketplacePaymentState.Paid
        )
        .reduce((sum, line) => sum + (line.amt?.amt ?? 0), 0);
      const balanceDue = totalAmount - paymentsMade;
      const chargesCredits = (sale.lineItems ?? []).map((li) => ({
        ...li,
        description: li.description ?? '',
        paymentReferenceId: li.paymentReferenceId ?? '',
        costType:
          li.costType === SaleCostType.Charge ? labels.charge : labels.credit,
        amount: (li.costType === SaleCostType.Credit ? -1 : 1) * li.cost,
      }));

      const invoiceLineItems = [
        { description: labels.proceeds, amount: totalAmount },
        ...chargesCredits,
      ];

      const blob = await pdf(
        <RetailInvoicePDF
          labels={labels}
          brokerName={loginContext?.user?.activeAccount?.accountName ?? ''}
          brokerEmail={loginContext?.user?.userName ?? undefined}
          buyerName={sale.transDest?.name ?? buyer?.name}
          buyerEmail={sale.transDest?.email ?? buyer?.email}
          buyerAddress={buyerAddress}
          invoiceNumber={sale.idOnMkp ?? `${sale.id}`}
          invoiceDate={sale.saleDate}
          currency={sale.currency}
          lineItems={invoiceLineItems}
          status={
            sale.pmtState === PointOfSalePaymentState.Paid
              ? 'Paid'
              : 'Outstanding'
          }
          totalAmount={totalAmount}
          balanceDue={balanceDue}
          paymentsMade={paymentsMade}
          brokerNotes={sale.privNotes ?? undefined}
          paymentMethod={paymentMethodDisplay}
          orderDetailRows={[
            {
              eventName: event?.name,
              eventDate: event?.dates?.start ?? undefined,
              venueName: venue?.name,
              section: sale.seating?.section ?? undefined,
              row: sale.seating?.row ?? undefined,
              seatFrom: sale.seating?.seatFr ?? undefined,
              seatTo: sale.seating?.seatTo ?? undefined,
              qty: sale.qtySold,
              proceed: totalAmount,
            },
          ]}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `RetailInvoice_${sale.id}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating retail invoice PDF:', error);
    } finally {
      setIsLoading(false);
    }
  }, [buyer, buyerPaymentMethods, sale, labels]);

  const iconFill =
    iconOnlyMode || (variant && variant !== 'regular')
      ? vars.color.textBrand
      : vars.color.textInverted;

  // POS-8630/POS-8856: while the group payload OR its batch catalog is in flight, keep
  // the button disabled so the group branch always has resolved events to build from
  // before it downloads. isPreparingGroup is only true on the group path (the shared
  // hook self-gates on the feature + a non-null salesGroupId otherwise).
  const buttonDisabled =
    disabled || isLoading || !eligibleToDownload || isPreparingGroup;

  const onDownloadClick = shouldUseGroupInvoice
    ? generateAndDownloadGroupPDF
    : generateAndDownloadRetailPDF;

  const showProcessing = isLoading || isPreparingGroup;

  return (
    <ButtonWithIcon
      disabled={buttonDisabled}
      variant={variant}
      style={variant === 'textPlain' ? { width: '100%' } : undefined}
      justifyContent="space-between"
      iconOnlyMode={iconOnlyMode}
      textContentId={ContentId.PrintInvoice}
      onClick={buttonDisabled ? undefined : onDownloadClick}
      icon={
        showProcessing ? (
          <RotatingWrapper>
            <ProcessingIcon fill={iconFill} />
          </RotatingWrapper>
        ) : (
          <DownloadIcon
            withHoverEffect
            disabled={buttonDisabled}
            fill={iconFill}
            size={vars.iconSize.m}
          />
        )
      }
    />
  );
};
